import { spErrorTags } from "@eventr/common";

// How long a read or write waits for the database to open before giving up. An open can
// queue behind a delete or upgrade that another tab is blocking, and never finish.
const OPEN_TIMEOUT_MS = 3000;

// IndexedDB wrapper class. Reads resolve undefined and writes resolve false while the
// database is not open, so a caller awaiting the cache never waits on it forever.
export class IndexedDB {
  db: IDBDatabase | null;
  private ready: Promise<IDBDatabase | null>;
  private closed = false;
  // connect to IndexedDB database
  constructor(dbName: any, dbVersion: any, dbUpgrade: any) {
    // connection object
    this.db = null;
    // no support
    if (!("indexedDB" in window)) {
      spErrorTags("IndexedDB not supported", ["IDB"]);
      throw new Error("IndexedDB not supported");
    }
    this.ready = new Promise((resolve) => {
      // open database
      const dbOpen = indexedDB.open(dbName, dbVersion);
      if (dbUpgrade) {
        // database upgrade event
        dbOpen.onupgradeneeded = (e) => {
          dbUpgrade(dbOpen.result, e.oldVersion, e.newVersion);
        };
      }
      dbOpen.onsuccess = () => {
        const db = dbOpen.result;
        // Closed while the open was pending, so release the connection it just made.
        if (this.closed) {
          db.close();
          resolve(null);
          return;
        }
        // Close on a delete or upgrade from this tab or another, which an open connection
        // blocks. Logout deletes the database, and the next open would queue behind it.
        db.onversionchange = () => this.close();
        this.db = db;
        resolve(db);
      };
      dbOpen.onerror = () => {
        spErrorTags("IndexedDB failed to open", ["IDB", dbName], dbOpen.error);
        resolve(null);
      };
      dbOpen.onblocked = () => {
        spErrorTags("IndexedDB open blocked by another connection", ["IDB", dbName]);
      };
    });
  }
  close() {
    this.closed = true;
    this.db?.close();
    this.db = null;
  }
  // The open database, or null if it did not open in time.
  private connection(): Promise<IDBDatabase | null> {
    if (this.db) return Promise.resolve(this.db);
    if (this.closed) return Promise.resolve(null);
    return Promise.race([
      this.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), OPEN_TIMEOUT_MS)),
    ]);
  }
  // store item
  async set(storeName: any, name: any, value: any) {
    const db = await this.connection();
    if (!db) {
      spErrorTags("IndexedDB not open", ["IDB"]);
      return false;
    }
    if (!db.objectStoreNames.contains(storeName)) {
      spErrorTags("IndexedDB store does not exist", ["IDB"]);
      return false;
    }
    return new Promise<boolean>((resolve, reject) => {
      // new transaction
      const transaction = db.transaction(storeName, "readwrite"),
        store = transaction.objectStore(storeName);
      // write record
      store.put(value, name);
      transaction.oncomplete = () => {
        resolve(true); // success
      };
      transaction.onerror = () => {
        reject(transaction.error); // failure
      };
    });
  }
  // get named item
  async get<T>(storeName: any, name: any) {
    const db = await this.connection();
    if (!db) {
      spErrorTags("IndexedDB not open", ["IDB"]);
      return undefined;
    }
    if (!db.objectStoreNames.contains(storeName)) {
      spErrorTags("IndexedDB store does not exist", ["IDB"]);
      return undefined;
    }
    return new Promise<T>((resolve, reject) => {
      // new transaction
      const transaction = db.transaction(storeName, "readonly"),
        store = transaction.objectStore(storeName),
        // read record
        request = store.get(name);
      request.onsuccess = () => {
        resolve(request.result as T); // success
      };
      request.onerror = () => {
        reject(request.error); // failure
      };
    });
  }
}
